import { CompleteAddress } from '@dreryk/address-common-api';
import {
   HealthcareFacilityUnitCodeNotUniqueFault,
   HealthcareFacilityUnitUpdateCommand,
   HealthcareOrganizationPartContactData,
} from '@dreryk/egabinet-healthcare-provider-frontend-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { commonTranslationKeys } from '@dreryk/react-common';
import { dispatchSnackbarPositiveEvent, ModalConfirmButton } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { healthcareOrganizationTranslationKeys } from '../../../../translations';
import { mapScopeOfProvidedServicesFormBeanPartToParameters } from '../../shared/scopeOfProvidedServices/ScopeOfProvidedServicesInteractiveTable.utils';

import {
   HealthcareFacilityUnitUpdateFormBean,
   HealthcareFacilityUnitUpdateFormController,
} from './form/HealthcareFacilityUnitUpdateForm';

type HealthcareFacilityUnitUpdateModalConfirmButtonProps = Pick<
   HealthcareFacilityUnitUpdateFormController,
   'handleSubmit'
> & {
   readonly onDataChanged: VoidFunction;
   readonly setUnitCodeNotUniqueErrorMessage: VoidFunction;
};

export const HealthcareFacilityUnitUpdateModalConfirmButton = (
   props: HealthcareFacilityUnitUpdateModalConfirmButtonProps
): JSX.Element => {
   const { executeAsync: updateHealthcareFacilityUnit } = useCqrsCommand<
      void,
      HealthcareFacilityUnitUpdateFormBean,
      HealthcareFacilityUnitCodeNotUniqueFault
   >(
      (parameters) => {
         const command = new HealthcareFacilityUnitUpdateCommand();
         command.id = parameters.id;
         command.name = parameters.name;
         command.unitCode = parameters.unitCode!;
         command.usingHealthcareFacilityAddressData = parameters.usingParentAddressData;
         if (!parameters.usingParentAddressData) {
            command.address = Choice.of<CompleteAddress>(
               CompleteAddress.builder()
                  .withTown(parameters.city!)
                  .withPostcode(parameters.postcode!)
                  .withStreet(parameters.street)
                  .withBuilding(parameters.house!)
                  .withFlat(parameters.flat)
                  .build()
            );
         }

         command.usingHealthcareFacilityContactData = parameters.usingParentContactData;
         if (!parameters.usingParentContactData) {
            command.contactData = Choice.of<HealthcareOrganizationPartContactData>({
               phoneNumber: parameters.phoneNumber!,
               emailAddress: Choice.withUseDefaultIfNull(parameters.emailAddress),
            });
         }

         command.scopeOfProvidedServicesParametersList = mapScopeOfProvidedServicesFormBeanPartToParameters(parameters);

         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(healthcareOrganizationTranslationKeys.healthcareFacilityUnitDataChanged);
            props.onDataChanged();
         },
         onFault: {
            'eGabinetHealthcareProvider:HealthcareFacilityUnitCodeNotUniqueFault': () =>
               props.setUnitCodeNotUniqueErrorMessage(),
         },
      }
   );

   return (
      <ModalConfirmButton
         label={commonTranslationKeys.save}
         onClick={props.handleSubmit(updateHealthcareFacilityUnit)}
      />
   );
};
